import type { TemplateNode } from "../components/types/template";
import type { PagedFlipPageKind } from "@/lib/mnrq/paged-flip/types";

const BADGE_STYLE = {
  color: "${colors.promoText}",
  backgroundColor: "${colors.promoBg}",
  borderColor: "${colors.promoBorder}",
  fontFamily: "${fonts.body}",
};

function isDynamicBinding(value: string) {
  return /\{[^}]+\}|\$\{[^}]+\}/.test(value);
}

function textNode(
  id: string,
  type: TemplateNode["type"],
  text: string,
  className: string,
  style: Record<string, string>,
  extraProps: Record<string, any> = {},
): TemplateNode {
  const mergedBindings = { ...(extraProps.bindings || {}) };
  if (isDynamicBinding(text)) {
    mergedBindings.text = text;
  }

  return {
    id,
    type,
    props: {
      className,
      style,
      ...extraProps,
      ...(isDynamicBinding(text) ? {} : { text }),
      ...(Object.keys(mergedBindings).length > 0 ? { bindings: mergedBindings } : {}),
    },
  };
}

function badgeNode(id: string, text: string, show: string): TemplateNode {
  const mergedBindings = isDynamicBinding(text) ? { text } : undefined;
  return {
    id,
    type: "span",
    props: {
      show,
      className: "inline-flex rounded-full border px-2.5 py-1 text-2xs font-semibold uppercase tracking-[0.16em]",
      style: BADGE_STYLE,
      ...(mergedBindings ? { bindings: mergedBindings } : { text }),
    },
  };
}

function buildMetaRows(prefix: string): TemplateNode {
  return {
    id: `${prefix}-meta-repeat`,
    type: "div",
    props: {
      repeater: true,
      bindings: { repeatFor: "page.metaRows" },
      className: "grid gap-2 sm:grid-cols-2",
    },
    children: [
      {
        id: `${prefix}-meta-card`,
        type: "div",
        props: {
          className: "rounded-2xl border px-3 py-2.5",
          style: {
            borderColor: "${colors.divider}",
            backgroundColor: "${colors.background}",
          },
        },
        children: [
          textNode(
            `${prefix}-meta-label`,
            "p",
            "{metaRow.label}",
            "text-2xs font-semibold uppercase tracking-[0.18em]",
            { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
          ),
          textNode(
            `${prefix}-meta-value`,
            "p",
            "{metaRow.value}",
            "mt-1 text-sm leading-6",
            { color: "${colors.itemTitle}", fontFamily: "${fonts.body}" },
          ),
        ],
      },
    ],
  };
}

function buildItemBadges(prefix: string): TemplateNode[] {
  return [
    badgeNode(`${prefix}-sold-out-badge`, "Sold Out", "{item.sellState === 'sold_out'}"),
    badgeNode(`${prefix}-paused-badge`, "Paused", "{item.sellState === 'paused'}"),
    badgeNode(
      `${prefix}-promo-badge`,
      "{item.promoLabel}",
      "{pagedFlip.features.showPromotionBadges && item.promoLabel && item.sellState !== 'sold_out' && item.sellState !== 'paused'}",
    ),
    badgeNode(`${prefix}-coming-badge`, "Coming Soon", "{item.comingSoon}"),
    badgeNode(`${prefix}-new-badge`, "New", "{item.isNew && !item.comingSoon}"),
    badgeNode(`${prefix}-featured-badge`, "Featured", "{item.featured}"),
  ];
}

function buildItemCard(prefix: string, background: string = "${colors.panel}"): TemplateNode {
  return {
    id: `${prefix}-item-card`,
    type: "div",
    props: {
      className: "rounded-2xl border px-4 py-3",
      style: {
        borderColor: "${colors.divider}",
        backgroundColor: background,
      },
      promoStyle: {
        borderColor: "${colors.promoBorder}",
        backgroundColor: "color-mix(in srgb, ${colors.promoBg} 48%, ${colors.panel})",
      },
    },
    children: [
      {
        id: `${prefix}-item-grid`,
        type: "div",
        props: {
          className:
            "grid gap-3 {item.image ? 'md:grid-cols-[72px_1fr_auto]' : 'md:grid-cols-[1fr_auto]'} md:items-start",
        },
        children: [
          {
            id: `${prefix}-item-image`,
            type: "img",
            props: {
              show: "{pagedFlip.features.showItemImages && item.image}",
              bindings: { src: "{item.image}", alt: "{item.title}" },
              className: "hidden h-[72px] w-[72px] rounded-2xl border object-cover md:block",
              style: {
                borderColor: "${colors.panelBorder}",
              },
            },
          },
          {
            id: `${prefix}-item-copy`,
            type: "div",
            props: { className: "min-w-0 space-y-2" },
            children: [
              {
                id: `${prefix}-item-head`,
                type: "div",
                props: { className: "flex flex-wrap items-center gap-2" },
                children: [
                  textNode(
                    `${prefix}-item-title`,
                    "p",
                    "{item.title}",
                    "text-sm font-semibold",
                    { color: "${colors.itemTitle}", fontFamily: "${fonts.item}" },
                  ),
                  {
                    id: `${prefix}-item-badges`,
                    type: "div",
                    props: { className: "flex flex-wrap gap-2" },
                    children: buildItemBadges(prefix),
                  },
                ],
              },
              textNode(
                `${prefix}-item-description`,
                "p",
                "{item.description}",
                "text-sm leading-6",
                { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
                { show: "{pagedFlip.features.showItemDescriptions && item.description}" },
              ),
            ],
          },
          textNode(
            `${prefix}-item-price`,
            "span",
            "{item.price}",
            "text-sm font-semibold md:pt-0.5",
            { color: "${colors.price}", fontFamily: "${fonts.price}" },
            { show: "{item.price}" },
          ),
        ],
      },
    ],
  };
}

const DEFAULT_PAGED_FLIP_SLOT_NODES: Record<PagedFlipPageKind, TemplateNode[]> = {
  cover: [
    {
      id: "paged-default-cover-root",
      type: "div",
      props: {
        className: "flex h-full flex-col justify-between rounded-2xl border px-7 py-8 md:px-10 md:py-10",
        style: {
          borderColor: "${colors.panelBorder}",
          backgroundColor: "${colors.panel}",
        },
      },
      children: [
        {
          id: "paged-default-cover-top",
          type: "div",
          props: { className: "space-y-5" },
          children: [
            {
              id: "paged-default-cover-kicker-row",
              type: "div",
              props: { className: "flex flex-wrap items-center gap-2" },
              children: [
                textNode(
                  "paged-default-cover-kicker",
                  "p",
                  "{page.eyebrow}",
                  "text-2xs font-semibold uppercase tracking-[0.24em]",
                  { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
                  { show: "{page.eyebrow}" },
                ),
                badgeNode(
                  "paged-default-cover-badge",
                  "{template.pagedFlip.cover.badge}",
                  "{template.pagedFlip.cover.badge}",
                ),
              ],
            },
            textNode(
              "paged-default-cover-title",
              "h1",
              "{page.title}",
              "max-w-[11ch] text-5xl leading-none tracking-tight md:text-6xl",
              { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
            ),
            textNode(
              "paged-default-cover-subtitle",
              "p",
              "{page.subtitle}",
              "max-w-[34rem] text-base leading-7",
              { color: "${colors.subheading}", fontFamily: "${fonts.subheading}" },
              { show: "{page.subtitle}" },
            ),
            buildMetaRows("paged-default-cover"),
          ],
        },
        {
          id: "paged-default-cover-bottom",
          type: "div",
          props: { className: "space-y-3" },
          children: [
            {
              id: "paged-default-cover-rule",
              type: "div",
              props: {
                className: "h-px w-20",
                style: { backgroundColor: "${colors.price}" },
              },
            },
            textNode(
              "paged-default-cover-note",
              "p",
              "{template.pagedFlip.cover.note || 'A live menu book generated from Menuraq data, built for a richer reading experience.'}",
              "max-w-[24rem] text-sm leading-6",
              { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
            ),
          ],
        },
      ],
    },
  ],
  toc: [
    {
      id: "paged-default-toc-root",
      type: "div",
      props: { className: "space-y-6" },
      children: [
        {
          id: "paged-default-toc-head",
          type: "div",
          props: { className: "space-y-2" },
          children: [
            textNode(
              "paged-default-toc-kicker",
              "p",
              "{page.eyebrow || 'Guide'}",
              "text-2xs font-semibold uppercase tracking-[0.24em]",
              { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
            ),
            textNode(
              "paged-default-toc-title",
              "h2",
              "{page.title}",
              "text-3xl tracking-tight",
              { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
            ),
            textNode(
              "paged-default-toc-subtitle",
              "p",
              "{page.subtitle}",
              "text-sm leading-6",
              { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
              { show: "{page.subtitle}" },
            ),
          ],
        },
        {
          id: "paged-default-toc-repeat",
          type: "div",
          props: {
            repeater: true,
            bindings: { repeatFor: "page.tocEntries" },
            className: "space-y-3",
          },
          children: [
            {
              id: "paged-default-toc-button",
              type: "button",
              props: {
                hotspot: "chapter",
                bindings: { targetId: "{tocEntry.id}" },
                className: "flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition hover:opacity-90",
                style: {
                  borderColor: "${colors.divider}",
                  backgroundColor: "${colors.panel}",
                },
              },
              children: [
                textNode(
                  "paged-default-toc-label",
                  "span",
                  "{tocEntry.label}",
                  "font-medium",
                  { color: "${colors.itemTitle}", fontFamily: "${fonts.item}" },
                ),
                textNode(
                  "paged-default-toc-page",
                  "span",
                  "Page {tocEntry.pageNumber}",
                  "text-xs uppercase tracking-[0.16em]",
                  { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
                ),
              ],
            },
          ],
        },
      ],
    },
  ],
  chapter: [
    {
      id: "paged-default-chapter-root",
      type: "div",
      props: { className: "flex h-full flex-col justify-center space-y-4" },
      children: [
        textNode(
          "paged-default-chapter-kicker",
          "p",
          "{page.eyebrow || 'Chapter'}",
          "text-2xs font-semibold uppercase tracking-[0.24em]",
          { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
        ),
        textNode(
          "paged-default-chapter-title",
          "h2",
          "{page.title}",
          "text-4xl tracking-tight md:text-5xl",
          { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
        ),
        textNode(
          "paged-default-chapter-subtitle",
          "p",
          "{page.subtitle}",
          "max-w-[28rem] text-base leading-7",
          { color: "${colors.subheading}", fontFamily: "${fonts.subheading}" },
          { show: "{page.subtitle}" },
        ),
      ],
    },
  ],
  "section-divider": [
    {
      id: "paged-default-section-root",
      type: "div",
      props: { className: "flex h-full flex-col justify-center space-y-5" },
      children: [
        textNode(
          "paged-default-section-kicker",
          "p",
          "{page.eyebrow || 'Section'}",
          "text-2xs font-semibold uppercase tracking-[0.24em]",
          { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
        ),
        {
          id: "paged-default-section-copy",
          type: "div",
          props: { className: "space-y-3" },
          children: [
            textNode(
              "paged-default-section-title",
              "h2",
              "{page.title}",
              "text-4xl tracking-tight",
              { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
            ),
            textNode(
              "paged-default-section-subtitle",
              "p",
              "{page.subtitle}",
              "max-w-[28rem] text-base leading-7",
              { color: "${colors.subheading}", fontFamily: "${fonts.subheading}" },
              { show: "{page.subtitle}" },
            ),
          ],
        },
        buildMetaRows("paged-default-section"),
      ],
    },
  ],
  "category-spotlight": [
    {
      id: "paged-default-spotlight-root",
      type: "div",
      props: { className: "grid h-full gap-6 md:grid-cols-[1.1fr_0.9fr]" },
      children: [
        {
          id: "paged-default-spotlight-image",
          type: "div",
          props: {
            show: "{pagedFlip.features.showCategorySpotlights && page.spotlightImage && (!pagedFlip.isMobile || !pagedFlip.features.mobileHideCategorySpotlight)}",
            className: "hidden rounded-2xl border md:block",
            style: {
              borderColor: "${colors.panelBorder}",
              backgroundImage:
                "linear-gradient(180deg, rgba(17,16,15,0.05), rgba(17,16,15,0.20)), url({page.spotlightImage})",
              backgroundSize: "cover",
              backgroundPosition: "center",
            },
          },
        },
        {
          id: "paged-default-spotlight-copy",
          type: "div",
          props: { className: "flex flex-col justify-center space-y-4" },
          children: [
            textNode(
              "paged-default-spotlight-kicker",
              "p",
              "{page.eyebrow || 'Category spotlight'}",
              "text-2xs font-semibold uppercase tracking-[0.24em]",
              { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
            ),
            textNode(
              "paged-default-spotlight-title",
              "h2",
              "{page.title}",
              "text-4xl tracking-tight md:text-5xl",
              { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
            ),
            textNode(
              "paged-default-spotlight-subtitle",
              "p",
              "{page.subtitle}",
              "max-w-[28rem] text-base leading-7",
              { color: "${colors.itemDesc}", fontFamily: "${fonts.subheading}" },
              { show: "{page.subtitle}" },
            ),
          ],
        },
      ],
    },
  ],
  "promotion-showcase": [
    {
      id: "paged-default-promotion-root",
      type: "div",
      props: { className: "space-y-5" },
      children: [
        textNode(
          "paged-default-promotion-kicker",
          "p",
          "{page.eyebrow || 'Promotions'}",
          "text-2xs font-semibold uppercase tracking-[0.24em]",
          { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
        ),
        textNode(
          "paged-default-promotion-title",
          "h2",
          "{page.title}",
          "text-3xl tracking-tight",
          { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
        ),
        textNode(
          "paged-default-promotion-subtitle",
          "p",
          "{page.subtitle}",
          "text-sm leading-6",
          { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
          { show: "{page.subtitle}" },
        ),
        {
          id: "paged-default-promotion-repeat",
          type: "div",
          props: {
            repeater: true,
            bindings: { repeatFor: "page.promotions" },
            className: "space-y-3",
          },
          children: [
            {
              id: "paged-default-promotion-card",
              type: "div",
              props: {
                className: "rounded-2xl border px-4 py-4",
                style: {
                  borderColor: "${colors.panelBorder}",
                  backgroundColor: "${colors.panel}",
                },
              },
              children: [
                textNode(
                  "paged-default-promotion-card-title",
                  "h3",
                  "{promotion.title}",
                  "text-lg font-semibold",
                  { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
                ),
                textNode(
                  "paged-default-promotion-card-description",
                  "p",
                  "{promotion.description}",
                  "mt-2 text-sm leading-6",
                  { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
                  { show: "{promotion.description}" },
                ),
              ],
            },
          ],
        },
      ],
    },
  ],
  "featured-showcase": [
    {
      id: "paged-default-featured-root",
      type: "div",
      props: { className: "space-y-5" },
      children: [
        textNode(
          "paged-default-featured-kicker",
          "p",
          "{page.eyebrow || 'Featured'}",
          "text-2xs font-semibold uppercase tracking-[0.24em]",
          { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
        ),
        textNode(
          "paged-default-featured-title",
          "h2",
          "{page.title}",
          "text-3xl tracking-tight",
          { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
        ),
        textNode(
          "paged-default-featured-subtitle",
          "p",
          "{page.subtitle}",
          "text-sm leading-6",
          { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
          { show: "{page.subtitle}" },
        ),
        {
          id: "paged-default-featured-repeat",
          type: "div",
          props: {
            repeater: true,
            bindings: { repeatFor: "page.categories" },
            className: "space-y-4",
          },
          children: [
            {
              id: "paged-default-featured-category-card",
              type: "div",
              props: {
                className: "rounded-2xl border p-4",
                style: {
                  borderColor: "${colors.panelBorder}",
                  backgroundColor: "${colors.panel}",
                },
              },
              children: [
                textNode(
                  "paged-default-featured-category-title",
                  "h3",
                  "{category.name}",
                  "text-xl tracking-tight",
                  { color: "${colors.sectionTitle}", fontFamily: "${fonts.heading}" },
                ),
                textNode(
                  "paged-default-featured-category-description",
                  "p",
                  "{category.description}",
                  "mt-1 text-sm leading-6",
                  { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
                  { show: "{category.description}" },
                ),
                {
                  id: "paged-default-featured-items-repeat",
                  type: "div",
                  props: {
                    repeater: true,
                    bindings: { repeatFor: "items" },
                    className: "mt-4 grid gap-3 md:grid-cols-2",
                  },
                  children: [buildItemCard("paged-default-featured", "${colors.background}")],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  "new-arrivals": [
    {
      id: "paged-default-new-root",
      type: "div",
      props: { className: "space-y-5" },
      children: [
        textNode(
          "paged-default-new-kicker",
          "p",
          "{page.eyebrow || 'New arrivals'}",
          "text-2xs font-semibold uppercase tracking-[0.24em]",
          { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
        ),
        textNode(
          "paged-default-new-title",
          "h2",
          "{page.title}",
          "text-3xl tracking-tight",
          { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
        ),
        textNode(
          "paged-default-new-subtitle",
          "p",
          "{page.subtitle}",
          "text-sm leading-6",
          { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
          { show: "{page.subtitle}" },
        ),
        {
          id: "paged-default-new-repeat",
          type: "div",
          props: {
            repeater: true,
            bindings: { repeatFor: "page.categories" },
            className: "space-y-4",
          },
          children: [
            {
              id: "paged-default-new-category-card",
              type: "div",
              props: {
                className: "rounded-2xl border p-4",
                style: {
                  borderColor: "${colors.panelBorder}",
                  backgroundColor: "${colors.panel}",
                },
              },
              children: [
                textNode(
                  "paged-default-new-category-title",
                  "h3",
                  "{category.name}",
                  "text-xl tracking-tight",
                  { color: "${colors.sectionTitle}", fontFamily: "${fonts.heading}" },
                ),
                textNode(
                  "paged-default-new-category-description",
                  "p",
                  "{category.description}",
                  "mt-1 text-sm leading-6",
                  { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
                  { show: "{category.description}" },
                ),
                {
                  id: "paged-default-new-items-repeat",
                  type: "div",
                  props: {
                    repeater: true,
                    bindings: { repeatFor: "items" },
                    className: "mt-4 grid gap-3 md:grid-cols-2",
                  },
                  children: [buildItemCard("paged-default-new", "${colors.background}")],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  content: [
    {
      id: "paged-default-content-root",
      type: "div",
      props: { className: "space-y-6" },
      children: [
        {
          id: "paged-default-content-head",
          type: "div",
          props: { className: "space-y-1" },
          children: [
            textNode(
              "paged-default-content-kicker",
              "p",
              "{page.eyebrow || page.chapterLabel || 'Menu'}",
              "text-2xs font-semibold uppercase tracking-[0.24em]",
              { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
            ),
            textNode(
              "paged-default-content-title",
              "h2",
              "{page.title}",
              "text-2xl tracking-tight",
              { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
              { show: "{page.title}" },
            ),
          ],
        },
        {
          id: "paged-default-content-category-repeat",
          type: "div",
          props: {
            repeater: true,
            bindings: { repeatFor: "page.categories" },
            className: "space-y-6",
          },
          children: [
            {
              id: "paged-default-content-category-shell",
              type: "section",
              props: { className: "space-y-4" },
              children: [
                {
                  id: "paged-default-content-category-head",
                  type: "div",
                  props: { className: "space-y-2" },
                  children: [
                    textNode(
                      "paged-default-content-category-name",
                      "h3",
                      "{category.name}",
                      "text-lg uppercase tracking-[0.12em]",
                      { color: "${colors.sectionTitle}", fontFamily: "${fonts.section}" },
                    ),
                    textNode(
                      "paged-default-content-category-description",
                      "p",
                      "{category.description}",
                      "text-sm leading-6",
                      { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
                      { show: "{category.description}" },
                    ),
                    {
                      id: "paged-default-content-category-status",
                      type: "div",
                      props: { className: "flex flex-wrap items-center gap-2" },
                      children: [
                        textNode(
                          "paged-default-content-category-range",
                          "span",
                          "Items {category.metrics.startsAt + 1}-{category.metrics.endsAt + 1} of {category.metrics.totalItems}",
                          "text-2xs font-semibold uppercase tracking-[0.14em]",
                          { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
                          { show: "{category.metrics && category.metrics.totalItems > category.metrics.itemCount}" },
                        ),
                        badgeNode(
                          "paged-default-content-category-continuation",
                          "Continued",
                          "{category.continuation}",
                        ),
                      ],
                    },
                  ],
                },
                {
                  id: "paged-default-content-items-repeat",
                  type: "div",
                  props: {
                    repeater: true,
                    bindings: { repeatFor: "items" },
                    className: "grid gap-3 {page.layout === 'two-column-items' ? 'md:grid-cols-2' : ''}",
                  },
                  children: [buildItemCard("paged-default-content")],
                },
              ],
            },
          ],
        },
        {
          id: "paged-default-content-empty",
          type: "div",
          props: {
            show: "{!page.categories || page.categories.length === 0}",
            className: "rounded-3xl border border-dashed px-4 py-6 text-sm",
            style: {
              borderColor: "${colors.divider}",
              color: "${colors.subheading}",
              fontFamily: "${fonts.body}",
            },
            text: "This page is ready, but the selected menu does not have enough content for chapter blocks yet.",
          },
        },
      ],
    },
  ],
  blank: [],
  "back-cover": [
    {
      id: "paged-default-back-root",
      type: "div",
      props: {
        className: "flex h-full flex-col justify-between rounded-2xl border px-7 py-8 md:px-10 md:py-10",
        style: {
          borderColor: "${colors.panelBorder}",
          backgroundColor: "${colors.panel}",
        },
      },
      children: [
        {
          id: "paged-default-back-top",
          type: "div",
          props: { className: "space-y-4" },
          children: [
            textNode(
              "paged-default-back-kicker",
              "p",
              "{page.eyebrow || 'Closing note'}",
              "text-2xs font-semibold uppercase tracking-[0.24em]",
              { color: "${colors.subheading}", fontFamily: "${fonts.body}" },
            ),
            textNode(
              "paged-default-back-title",
              "h2",
              "{page.title}",
              "max-w-[14ch] text-4xl tracking-tight",
              { color: "${colors.heading}", fontFamily: "${fonts.heading}" },
            ),
          ],
        },
        {
          id: "paged-default-back-bottom",
          type: "div",
          props: { className: "space-y-4" },
          children: [
            textNode(
              "paged-default-back-subtitle",
              "p",
              "{page.subtitle}",
              "max-w-[24rem] text-base leading-7",
              { color: "${colors.itemDesc}", fontFamily: "${fonts.body}" },
              { show: "{page.subtitle}" },
            ),
            buildMetaRows("paged-default-back"),
            textNode(
              "paged-default-back-footer",
              "p",
              "{template.pagedFlip.backCover.footer}",
              "text-sm",
              { color: "${colors.price}", fontFamily: "${fonts.body}" },
              { show: "{template.pagedFlip.backCover.footer}" },
            ),
          ],
        },
      ],
    },
  ],
};

export function getDefaultPagedFlipSlotNodes(slot: PagedFlipPageKind): TemplateNode[] {
  return JSON.parse(JSON.stringify(DEFAULT_PAGED_FLIP_SLOT_NODES[slot] || []));
}
 
